// High-level functions for manipulating and interpreting
// a compute graph.
//
// User code (UI) uses the functions in this file to manipulate and
// interact with the graph.

import * as _ from 'lodash';
import * as LL from './graph';
import * as Types from '../types';
import * as String from '../../../util/string';
import * as TypeHelpers from '../typeHelpers';
import {ResolverContext} from './resolverContext';

// Functions here assume that all ops have at least one input.
// Because functions must take at least one argument,
// otherwise, they're not a function, they're a constant. (Note:
// in normal programming languages, its perfectly acceptable to
// have zero-argument functions, but since we don't allow
// side-effects, they'd be useless here).

// For editing we use a graph definition that allows void nodes
// in op inputs

export interface EditingOutputNode<T extends Types.Type = Types.Type>
  extends LL.BaseNode<T> {
  nodeType: 'output';
  fromOp: EditingOp;
}

export type EditingNode<T extends Types.Type = Types.Type> =
  | EditingOutputNode<T>
  | LL.ConstNode<T>
  | LL.VarNode<T>
  | LL.VoidNode;

export type EditingOpInputs = {[key: string]: EditingNode};

export interface EditingOp {
  name: string;
  inputs: EditingOpInputs;
}

export function opInputsAreNonVoid(
  opInputs: EditingOpInputs
): opInputs is LL.OpInputs {
  return !Object.values(opInputs).some(n => n.nodeType === 'void');
}

export function nodeIsExecutable(node: EditingNode): node is LL.NodeOrVoidNode {
  const voidCount = filterNodes(node, n => n.nodeType === 'void').length;
  return voidCount === 0 || (voidCount === 1 && node.nodeType === 'void');
}

export function isChainedUnaryOp(op: EditingOp) {
  if (isBinaryOp(op)) {
    return false;
  }

  const argValues = Object.values(op.inputs);
  return argValues[0].nodeType === 'var' || argValues[0].nodeType === 'output';
}

const BINARY_OP_INFO: {[key: string]: string} = {
  'number-equal': '=',
  'number-notequal': '!=',
  'number-add': '+',
  'number-sub': '-',
  'number-mult': '*',
  'number-div': '/',
  'number-greater': '>',
  'number-less': '<',
  'number-less-equal': '<=',
  'number-greater-equal': '>=',
  'string-equal': '=',
  'string-notequal': '!=',
  and: 'and',
  or: 'or',
};

export function isBinaryOp(op: {name: string}) {
  return BINARY_OP_INFO[op.name] != null;
}

export function isBooleanCombOp(op: {name: string}) {
  return op.name === 'and' || op.name === 'or';
}

export function isFunctionOp(op: {name: string}) {
  return !isBinaryOp(op) && !isBooleanCombOp(op);
}

export function binaryOpSymbol(op: {name: string}) {
  return BINARY_OP_INFO[op.name];
}

// Depth-first
export function mapNodes(
  node: EditingNode,
  mapFn: (inNode: EditingNode) => EditingNode
): EditingNode {
  if (node.nodeType === 'output') {
    const newInputs = _.mapValues(node.fromOp.inputs, inNode => {
      const mappedNode = mapNodes(inNode, mapFn);
      // if (mappedNode.nodeType === 'void') {
      //   throw new Error('encountered void node while mapping');
      // }
      return mappedNode;
    });
    // Only replace node if an input has changed.
    // This way you can implement a findAndReplaceNode with mapNodes.
    let replace = false;
    for (const argName of Object.keys(node.fromOp.inputs)) {
      if (newInputs[argName] !== node.fromOp.inputs[argName]) {
        replace = true;
      }
    }
    if (!replace) {
      return mapFn(node);
    } else {
      return mapFn({
        ...node,
        fromOp: {
          ...node.fromOp,
          inputs: newInputs as any,
        },
      });
    }
  } else {
    return mapFn(node);
  }
}

export async function mapNodesAsync(
  node: EditingNode,
  mapFn: (inNode: EditingNode) => Promise<EditingNode>
): Promise<EditingNode> {
  if (node.nodeType === 'output') {
    const mappedInputValues = await Promise.all(
      Object.values(node.fromOp.inputs).map(inNode =>
        mapNodesAsync(inNode, mapFn)
      )
    );
    const inputs: EditingOpInputs = {};
    const argNames = Object.keys(node.fromOp.inputs);
    for (let i = 0; i < argNames.length; i++) {
      inputs[argNames[i]] = mappedInputValues[i];
    }
    return await mapFn({
      ...node,
      fromOp: {
        ...node.fromOp,
        inputs,
      },
    });
  } else {
    return await mapFn(node);
  }
}

// This function is algorithmically inefficent right now (it applies the filter
// function way too many times)
export function filterNodes(
  node: EditingNode,
  filterFn: (inNode: EditingNode) => boolean
): EditingNode[] {
  let result: EditingNode[] = filterFn(node) ? [node] : [];
  if (node.nodeType === 'output') {
    const childNodes = Object.values(node.fromOp.inputs).flatMap(inNode =>
      filterNodes(inNode, filterFn)
    );
    result = result.concat(childNodes.filter(filterFn));
  }
  return result;
}

// Throws an error if not found
export function replaceNode(
  node: EditingNode,
  toReplace: EditingNode,
  replaceWith: EditingNode
): EditingNode {
  const result = mapNodes(node, checkNode =>
    checkNode === toReplace ? replaceWith : checkNode
  );
  if (result === node) {
    // nothing changed!
    throw new Error('didnt find node to replace');
  }
  return result;
}

export function maybeReplaceNode(
  node: EditingNode,
  toReplace: EditingNode,
  replaceWith: EditingNode
): EditingNode {
  return mapNodes(node, checkNode =>
    checkNode === toReplace ? replaceWith : checkNode
  );
}

// TODO: wire this to client so that it reuses graph cache
export async function refineEditingNodeType(
  context: ResolverContext,
  node: EditingNode
): Promise<EditingNode> {
  if (node.nodeType !== 'output') {
    return node;
  }
  const op = node.fromOp;
  const opDef = LL.getOpDef(op.name);

  // Otherwise improve all of this ops inputs first.
  const argNames = Object.keys(op.inputs);
  const argValues = Object.values(op.inputs);
  const allImprovedNodes = await Promise.all(
    argValues.map(n =>
      (n.nodeType as any) === 'void'
        ? n
        : n.nodeType === 'output'
        ? refineEditingNodeType(context, n)
        : n
    )
  );
  const newTypedInputs: EditingOpInputs = {};
  argNames.forEach((name, i) => (newTypedInputs[name] = allImprovedNodes[i]));
  if (!opInputsAreNonVoid(newTypedInputs)) {
    return Promise.resolve({
      nodeType: 'output',
      fromOp: {
        ...op,
        inputs: newTypedInputs,
      },
      type: 'void',
    });
  }

  const opWithImprovedInputs = {
    ...op,
    inputs: newTypedInputs,
  };
  if (opDef.resolveOutputType != null) {
    return opDef.resolveOutputType(opWithImprovedInputs, context);
  }
  return Promise.resolve({
    nodeType: 'output',
    fromOp: opWithImprovedInputs,
    type:
      typeof opDef.outputType === 'function'
        ? opDef.outputType(newTypedInputs)
        : opDef.outputType,
  });
}

export async function refineNodeType(
  context: ResolverContext,
  node: LL.Node
): Promise<LL.Node> {
  if (node.nodeType !== 'output') {
    return node;
  }
  return (await refineEditingNodeType(context, node)) as LL.Node;
}

export async function replaceNodeAndUpdateDownstreamTypes(
  context: ResolverContext,
  graph: EditingNode,
  toReplace: EditingNode,
  replaceWith: EditingNode
) {
  // This function is tricky, we want to ensure that replaceWith
  // is actually in the graph (by reference) at the end of this call
  // but refineEditingNodeType can replace all output nodes. So if
  // we have an output node, do something special, swap it with a
  // variable, and then swap at back after refineEditingNodeType.

  graph = replaceNode(
    graph,
    toReplace,
    replaceWith.nodeType !== 'output'
      ? replaceWith
      : LL.varNode(replaceWith.type, '_swapVar')
  );

  graph = await refineEditingNodeType(context, graph);

  if (replaceWith.nodeType === 'output') {
    graph = dereferenceVariables(graph, {
      _swapVar: replaceWith,
    }).node;
  }
  return graph;
}

// Return all expression nodes in the order they're displayed.
export function textOrderedNodes(node: EditingNode): EditingNode[] {
  if (node.nodeType !== 'output') {
    return [node];
  }
  return Object.values(node.fromOp.inputs).flatMap(textOrderedNodes);
}

export function dereferenceVariables(
  functionNode: EditingNode,
  variables: {[argName: string]: EditingNode}
) {
  const usedVarNames = new Set<string>();
  // walk function node, finding ops that have var inputs
  const node = mapNodes(functionNode, n => {
    if (n.nodeType === 'var') {
      const swapNode = variables[n.varName];
      if (swapNode == null) {
        return n;
        // throw new Error(
        //   'variable not in frame: ' +
        //     n.varName +
        //     ', available: ' +
        //     Object.keys(variables)
        // );
      }
      usedVarNames.add(n.varName);
      return swapNode;
    }
    return n;
  });
  return {node, usedVarNames: Array.from(usedVarNames)};
}

// Given a set of arguments, which are represented as nodes,
// and a function, which is a node that terminates at variables or
// constants, construct a new node that represents the function call
export function callFunction(
  functionNode: LL.Node,
  inputs: {[argName: string]: LL.Node}
): LL.Node {
  const {node, usedVarNames} = dereferenceVariables(functionNode, inputs);
  if (usedVarNames.length !== Object.keys(inputs).length) {
    console.log(
      'CALL ERROR',
      inputs,
      JSON.stringify(node, undefined, 2),
      usedVarNames
    );
    throw new Error('not all function call inputs used');
  }
  return node as LL.Node;
}

// Given a node that represents a function (a node with at least
// one variable ancestor), update the types of the variables, given
// inputs that have the desired types.
export function updateFunctionInputTypes(
  functionNode: LL.Node,
  inputs: {[argName: string]: LL.Node}
) {
  const usedVarNames = new Set<string>();
  // walk function node, finding ops that have var inputs
  const node = mapNodes(functionNode, n => {
    if (n.nodeType === 'var') {
      const inputForVar = inputs[n.varName];
      if (inputForVar == null) {
        throw new Error(
          'variable not in frame: ' +
            n.varName +
            ', available: ' +
            Object.keys(inputs)
        );
      }
      usedVarNames.add(n.varName);
      return {
        ...n,
        type: inputs[n.varName].type,
      };
    }
    return n;
  }) as LL.Node;
  return {node, usedVarNames: Array.from(usedVarNames)};
}

export function opDisplayName(op: {name: string}): string {
  if (isBinaryOp(op)) {
    return binaryOpSymbol(op);
  }
  if (op.name.indexOf('-') !== -1) {
    return String.splitOnce(op.name, '-')[1] as string;
  }
  return op.name;
}

export function getNodeResult(forwardGraph: LL.ForwardGraph, node: LL.Node) {
  if (node?.nodeType === 'output') {
    return forwardGraph.ops.get(node.fromOp)?.outputNode.result;
  } else if (node?.nodeType === 'const') {
    return node.val;
  }
  throw new Error('invalid, cant get value for node');
}

// Returns the list of ops that are available to handle a set of
// types. All op inputs must be fillable with either a type from
// requiredTypes or producibleTypes. requiredTypes must all be handled
// for a given op to be valid.
export function availableOps(
  requiredTypes: Types.Type[],
  producibleTypes: Types.Type[]
) {
  return Object.values(LL.registeredOps).filter(op => {
    const remainingRequiredTypes = [...requiredTypes];
    for (const it of Object.values(op.inputTypes)) {
      const rtIndex = remainingRequiredTypes.findIndex(rt =>
        Types.isAssignableTo(rt, it)
      );
      if (rtIndex !== -1) {
        remainingRequiredTypes.splice(rtIndex, 1);
      } else {
        if (!producibleTypes.some(pt => Types.isAssignableTo(pt, it))) {
          return false;
        }
      }
    }
    if (remainingRequiredTypes.length > 0) {
      return false;
    }
    return true;
  });
}

const PRIMITIVE_TYPES: Types.Type[] = ['string', 'number', 'boolean'];
function isPrimitiveType(type: Types.Type) {
  return PRIMITIVE_TYPES.includes(type);
}

export function isValidRootOp(opDef: LL.OpDefinition) {
  const argTypes = Object.values(opDef.inputTypes);
  return (
    argTypes.every(isPrimitiveType) &&
    typeof opDef.outputType !== 'function' &&
    !isPrimitiveType(opDef.outputType)
  );
}

export function rootOps() {
  return Object.values(LL.registeredOps).filter(isValidRootOp);
}

export function availableOpsForChain(
  varNodeType: Types.Type
): LL.OpDefinition[] {
  const avail = availableOps([varNodeType], PRIMITIVE_TYPES);
  if (isPrimitiveType(varNodeType)) {
    const res = avail.filter(o => !isValidRootOp(o));
    return res;
  }
  return avail;
}

export function pickSuggestions(objType: Types.Type): string[] {
  // Pick can handle one level of array and tagged values
  // TODO: make it more generic
  if (Types.isTaggedValue(objType)) {
    objType = objType.value;
  }
  if (Types.isArray(objType)) {
    objType = objType.objectType;
  }
  if (Types.isTaggedValue(objType)) {
    objType = objType.value;
  }
  let keys: string[] = [];
  if (Types.isObject(objType)) {
    const allPaths = TypeHelpers.allObjPaths(objType).map(pt => pt.path);
    // If we have nested paths that share the same tail key, included *. paths
    // in suggestions
    const sameLengthAndTailGroups = _.groupBy(
      allPaths,
      path => `${path.length}-${path[path.length - 1]}`
    );
    keys = Object.values(sameLengthAndTailGroups)
      .flatMap(paths => {
        if (paths.length === 1) {
          return paths;
        }
        const path0 = paths[0];
        const starredPath = _.range(path0.length - 1)
          .map(i => '*')
          .concat([path0[path0.length - 1]]);
        return [starredPath].concat(paths);
      })
      .map(path => path.join('.'));
  }
  return keys;
}

export function callOp(
  opName: string,
  inputs: EditingOpInputs
): EditingOutputNode {
  return {
    nodeType: 'output',
    type: 'any',
    fromOp: {
      name: opName,
      inputs,
    },
  };
}

export function constString(s: string): LL.ConstNode<'string'> {
  return {
    nodeType: 'const',
    type: 'string',
    val: s,
  };
}

export function constNumber(n: number): LL.ConstNode<'number'> {
  return {
    nodeType: 'const',
    type: 'number',
    val: n,
  };
}

export function constNodeUnsafe<T extends Types.Type>(type: T, value: any) {
  return {
    nodeType: 'const' as const,
    type,
    val: value,
  };
}

export function findConsumingOp(
  node: EditingNode,
  graph: EditingNode
):
  | {argIndex: number; argName: string; outputNode: EditingOutputNode}
  | undefined {
  const consumingOps = filterNodes(graph as any, checkNode => {
    if (checkNode.nodeType === 'output') {
      const innerArgNodes = Object.values(checkNode.fromOp.inputs);
      return innerArgNodes.find(n => n === node) != null;
    }
    return false;
  });
  if (consumingOps.length === 0) {
    return undefined;
  }
  if (consumingOps.length > 1) {
    throw new Error('too many consuming ops, this is not an expression');
  }
  const consumingOpNode = consumingOps[0];
  if (consumingOpNode.nodeType !== 'output') {
    // impossible, we just checked above.
    throw new Error('invalid');
  }
  const argNames = Object.keys(consumingOpNode.fromOp.inputs);
  const argNodes = Object.values(consumingOpNode.fromOp.inputs);
  const argIndex = argNodes.findIndex(n => n === node);
  return {argIndex, argName: argNames[argIndex], outputNode: consumingOpNode};
}

/**
 * Return the node representing the rightmost non-void element in the rendered
 * expression represented by node. Easier to understand by example -- see hl.test.ts.
 *
 * The intention is to return the thing that should "disappear" (from the user's
 * perspective), when they press backspace. Note that removing this element is
 * often more complicated than just voiding out its node -- see
 * expEditorState.tsx:deletePrev for details.
 */
export function rightMostAncestor(node: EditingNode): EditingNode {
  if (node.nodeType !== 'output') {
    // only output nodes have ancestors, so this is the
    // base case
    return node;
  }

  const op = node.fromOp;
  const argNodes = Object.values(op.inputs);

  if (isChainedUnaryOp(op) && op.name !== 'pick') {
    // either this unary op takes no arguments besides the previous chain,
    // or all arguments have already been voided out
    const argNodesAfterDotAreVoid =
      argNodes.slice(1).filter(argNode => argNode.nodeType !== 'void')
        .length === 0;

    if (argNodesAfterDotAreVoid) {
      return node;
    }
  } else if (isFunctionOp(op)) {
    // either this function takes no arguments,
    // or all arguments have already been voided out
    const argNodesAreVoid =
      argNodes.filter(argNode => argNode.nodeType !== 'void').length === 0;

    if (argNodesAreVoid) {
      return node;
    }
  }

  const rightMostArgNode = argNodes[argNodes.length - 1];
  return rightMostAncestor(rightMostArgNode);
}

// Given a function (a node which has at least one variable ancestor)
// update every node in the function graph with new types given
// inputs for the function.
export async function updateFunctionForInputs(
  context: ResolverContext,
  fnNode: LL.OutputNode,
  inputs: {[argName: string]: LL.Node}
) {
  // First, just update the function's variable nodes with the new types.
  const {node: withUpdatedInputs, usedVarNames} = updateFunctionInputTypes(
    fnNode,
    inputs
  );
  if (usedVarNames.length !== Object.keys(inputs).length) {
    console.log(
      'updateFunctionForInputs error',
      inputs,
      toString(fnNode),
      usedVarNames
    );
    throw new Error('not all function inputs used');
  }

  // Second, use improveOutputNode on the function's output to recursively
  // update all types within the function (including the final output node).
  if (withUpdatedInputs.nodeType !== 'output') {
    return Promise.resolve(withUpdatedInputs);
  }
  const improvedNode = await refineNodeType(context, withUpdatedInputs);
  return Promise.resolve(improvedNode);
}

export function opToString(op: EditingOp, indent: number = 0): string {
  const argValues = Object.values(op.inputs);
  const nodeToString = (node: EditingNode) => {
    return toString(node, indent);
  };
  if (isBooleanCombOp(op)) {
    return argValues.map(nodeToString).join(` ${binaryOpSymbol(op)} `);
  } else if (isBinaryOp(op)) {
    return `${nodeToString(argValues[0])} ${binaryOpSymbol(op)} ${nodeToString(
      argValues[1]
    )}`;
  } else if (isChainedUnaryOp(op)) {
    if (op.name === 'pick') {
      return `${nodeToString(argValues[0])}[${nodeToString(argValues[1])}]`;
    }
    const unaryOpArgs = argValues
      .slice(1)
      .map(nodeToString)
      .join(', ');
    return `${nodeToString(argValues[0])}\n  .${opDisplayName(
      op
    )}(${unaryOpArgs})`;
  }
  const opArgs = argValues
    .map(nodeToString)
    .map(argLinesString =>
      argLinesString
        .split('\n')
        .map(v => String.indent(v, 2))
        .join('\n')
    )
    .join(',\n');
  return `${opDisplayName(op)}(\n${opArgs})`;
}

export function toString(node: EditingNode, indent: number = 0): string {
  if (node.nodeType === 'const') {
    if (node.val != null && node.val.fromOp != null) {
      indent += 2;
      // This is a function argument
      const fnString = toString(node.val, indent);
      const lines = fnString
        .split('\n')
        .map((l, i) => (i === 0 ? l : String.indent(l, indent)))
        .join('\n');
      return lines;
    }
    return JSON.stringify(node.val);
  } else if (node.nodeType === 'void') {
    return '';
  } else if (node.nodeType === 'var') {
    // TODO: Big hack, if we encounter a var, make it look like a function
    // call. This won't work at all in scenarios where we encounter more
    // than one var!
    // TODO: Broke this hack in this commit
    return `${node.varName}`;
  } else if (node.nodeType == null) {
    // TODO: in the join op `ops.ts#opJoin`, the return type has 6 keys,
    // 2 of which (arr1 and arr2) end up being objectTypes, but lack the
    // fromOp and nodeType keys. We should resolve that which would remove this
    // branch of the conditional.
    return '';
  } else {
    return opToString(node.fromOp);
  }
}
